import {
  ApplicationConfig,
  APP_INITIALIZER,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withViewTransitions, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts';
import { appRoutes } from './app.routes';
import { KeycloakService } from 'keycloak-angular';
import {
  API_BASE_URL,
  initializeKeycloak,
  KEYCLOAK_CONFIG,
  authTokenInterceptor,
  errorInterceptor,
  changeDetectionInterceptor,
} from '@angular-workspace/core';
import { 
  USER_API_PATH, 
  ROLES_API_PATH, 
  ROLE_UPGRADE_API_PATH,
  AUTH_API_PATH,
  CAT_API_PATH,
  CATALOGUE_TEXT_SEARCH_OPTIONS_ENABLED,
  AUDITING_API_PATH,
  ACL_APD_API_PATH,
  FILE_BASE_URL,
  FILE_API_PATH,
  LEADERSHIP_API_PATH,
  SANDBOX_BASE_URL,
  USER_INTERACTION_API_PATH,
  COMMUNITY_BASE_URL,
  CHALLENGE_BASE_URL,
  RS_BASE_URL,
  OGC_TILES_BASE_URL,
  OGC_API_BASE_URL,
  OGC_EXECUTION_CONFIG,
} from '@angular-workspace/services';
import { OGC_MAPS_RESOURCE_SERVER_URLS, OGC_TILES_COLLECTION_ID, CANVAS_NOTIFIER, CHARTS_RESOURCE_SERVER_URLS, CANVAS_BOUNDARY_GEOJSON_URL } from '@angular-workspace/feature/canvas';
import { RESOURCE_SERVER_URL_CONFIG, resolveResourceServerUrlConfig, resolveCatalogueApdUrl, CATALOGUE_APD_URL, API_EXPLORER_SERVER_URL, API_EXPLORER_THEME, SANDBOX_DASHBOARD_THEME } from '@angular-workspace/feature';
import { environment } from '../environments/environment';
import { AuthListenerService } from './services/auth-listener.service';
import { GoogleAnalyticsService } from './services/google-analytics.service';
import { ToasterService } from './services/toaster.service';
import { MAHAAGX_API_EXPLORER_THEME } from './api-explorer/api-explorer-theme';
import { MAHAAGX_SANDBOX_DASHBOARD_THEME } from './platform-admin/dashboard/sandbox-dashboard-theme';
import { delegationIdInterceptor } from './interceptors/delegation-id.interceptor';

function keycloakInitializer(keycloak: KeycloakService) {
  return initializeKeycloak(keycloak, environment.keycloak);
}

function authListenerInitializer(authListener: AuthListenerService) {
  // Await so localStorage userData and RolesApiService match the API before first navigation
  return () => authListener.initialize();
}

function googleAnalyticsInitializer(analytics: GoogleAnalyticsService) {
  return () => {
    setTimeout(() => analytics.initialize(), 0);
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(), // Required for PrimeNG animations
    provideEchartsCore({ echarts }),
    provideRouter(
      appRoutes,
      withViewTransitions(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      })
    ),
    provideHttpClient(
      withInterceptors([
        authTokenInterceptor,
        delegationIdInterceptor,
        errorInterceptor,
        changeDetectionInterceptor,
      ])
    ),
    { provide: API_BASE_URL, useValue: environment.basePathUrl },
    { provide: KEYCLOAK_CONFIG, useValue: environment.keycloak },
    { provide: USER_API_PATH, useValue: environment.userApiPath },
    { provide: ROLES_API_PATH, useValue: environment.rolesApiPath },
    { provide: ROLE_UPGRADE_API_PATH, useValue: environment.roleUpgradeApiPath },
    { provide: AUTH_API_PATH, useValue: environment.authApiPath },
    { provide: CAT_API_PATH, useValue: environment.catApiPath },
    {
      provide: CATALOGUE_TEXT_SEARCH_OPTIONS_ENABLED,
      useValue: environment.catalogueTextSearchOptionsEnabled !== false,
    },
    { provide: AUDITING_API_PATH, useValue: environment.auditingApiPath },
    { provide: ACL_APD_API_PATH, useValue: environment.aclapdPath },
    { provide: FILE_BASE_URL, useValue: environment.baseFilePathUrl },
    { provide: FILE_API_PATH, useValue: '' }, // Empty string since baseFilePathUrl already includes /v1/
    { provide: RS_BASE_URL, useValue: environment.baseRsPathUrl },
    {
      provide: RESOURCE_SERVER_URL_CONFIG,
      useFactory: () => resolveResourceServerUrlConfig(environment),
    },
    {
      provide: CATALOGUE_APD_URL,
      useFactory: () => resolveCatalogueApdUrl(environment.basePathUrl),
    },
    { provide: OGC_TILES_BASE_URL, useValue: environment.ogcApiPath ?? environment.api_ogc_server_domain },
    { provide: OGC_API_BASE_URL, useValue: environment.ogcApiPath ?? environment.api_ogc_server_domain },
    {
      provide: OGC_EXECUTION_CONFIG,
      useValue: {
        bucketName: environment.ogcS3BucketName ?? '',
        region: environment.ogcS3Region ?? '',
        fileType: environment.ogcFileType ?? 'GeoPackage',
        version: environment.ogcVersion ?? '1.0.0',
        s3BucketIdentifier: environment.ogcS3BucketIdentifier ?? 'default',
      },
    },
    // Full URL to MahaAgX's own boundary GeoJSON served from its assets folder.
    // The file lives at apps/MahaAgX/src/assets/canvas-map/maharashtra-boundaries.geojson
    // and is copied to /assets/canvas-map/ at build time via project.json.
    { provide: CANVAS_BOUNDARY_GEOJSON_URL, useValue: '/assets/canvas-map/maharashtra-boundaries.geojson' },
    // OGC collection ID for the "Open Series" basemap tile layer in Canvas.
    // Replace with the MahaAgX-specific collection UUID when available.
    { provide: OGC_TILES_COLLECTION_ID, useValue: 'e9120dac-0700-4a41-b07e-277b4f94bad0' },
    // OGC resource server URLs used to filter datasets shown in the maps canvas modal.
    // Strip protocol (https?://) and trailing slash so values match the catalogue's
    // resourceServer.url field which stores bare hostnames (e.g. "v2.dev.ogc-s3.iudx.io").
    {
      provide: OGC_MAPS_RESOURCE_SERVER_URLS,
      useFactory: () =>
        [...new Set(
          [environment.ogcMapsResourceServerUrl, environment.api_ogc_server_domain]
            .map((url) => url?.trim().replace(/^https?:\/\//, '').replace(/\/$/, ''))
            .filter((url): url is string => Boolean(url)),
        )],
    },
    // RS resource server URL used to filter datasets shown in the charts canvas modal.
    {
      provide: CHARTS_RESOURCE_SERVER_URLS,
      useFactory: () =>
        [...new Set(
          [environment.baseRsPathUrl]
            .map((url) => url?.trim().replace(/^https?:\/\//, '').replace(/\/$/, ''))
            .filter((url): url is string => Boolean(url)),
        )],
    },
    {
      provide: CANVAS_NOTIFIER,
      useFactory: (toaster: ToasterService) => ({
        success: (message: string) => toaster.success(message),
        error: (message: string) => toaster.error(message),
      }),
      deps: [ToasterService],
    },
    { provide: LEADERSHIP_API_PATH, useValue: environment.leadershipApiPath },
    { provide: SANDBOX_BASE_URL, useValue: environment.baseSandboxPathUrlCpu },
    { provide: USER_INTERACTION_API_PATH, useValue: environment.userInteractionApiPath },
    { provide: COMMUNITY_BASE_URL, useValue: environment.baseCommunityPathUrl },
    { provide: CHALLENGE_BASE_URL, useValue: environment.baseChallengePathUrl },
    { provide: API_EXPLORER_THEME, useValue: MAHAAGX_API_EXPLORER_THEME },
    { provide: SANDBOX_DASHBOARD_THEME, useValue: MAHAAGX_SANDBOX_DASHBOARD_THEME },
    { provide: API_EXPLORER_SERVER_URL, useValue: environment.apiExplorerServerUrl ?? '' },
    KeycloakService,
    AuthListenerService,
    {
      provide: APP_INITIALIZER,
      useFactory: keycloakInitializer,
      multi: true,
      deps: [KeycloakService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: authListenerInitializer,
      multi: true,
      deps: [AuthListenerService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: googleAnalyticsInitializer,
      multi: true,
      deps: [GoogleAnalyticsService],
    },
  ],
};
